import { Page } from '@components/Page'
import { Breadcrumbs } from '@components/_rcpo-v6/Breadcrumbs'
import CheckerboardItem from '@components/_rcpo-v6/CheckerboardItem'
import { CollectionBanner } from '@components/_rcpo-v6/CollectionBanner'
import ContactForm from '@components/_rcpo-v6/ContactForm'
import ImageBanner from '@components/_rcpo-v6/ImageBanner'
import { RCPOPage } from '@components/_rcpo-v6/Page'
import ProductInfo from '@components/_rcpo-v6/ProductInfo'
import { hasSufficientProductData } from '@components/_rcpo-v6/ProductOverview/helpers/has-sufficient-product-data'
import { RCPO_ATTRIBUTE_ID_COLLECTION } from '@components/_rcpo-v6/ProductOverview/hooks/use-rcpo-data'
import ProductProperties from '@components/_rcpo-v6/ProductProperties'
import MayAlsoLike from '@components/_rcpo-v6/Sliders/MayAlsoLike'
import { SchemaOrg } from '@components/schema-org'
import { Article, ArticleDocument } from '@generated'
import { buildLanguageAlternateNavigation } from '@lib/build-language-alternates'
import { getRcpoModelSeoAttributes } from '@lib/get-rcpo-model-seo-attributes'
import { getServerSideTranslations } from '@lib/get-server-side-translations'
import { SingleItemResponse, getPageByCodename } from '@lib/kontent'
import { RolexRcpoModelPage } from '@lib/kontent/models/RolexRcpoModelPage'
import { createUrqlClient } from '@lib/urqlClient'
import { GetStaticProps, NextPage } from 'next'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import slugify from 'slugify'

type RCPOWatchProps = SingleItemResponse<RolexRcpoModelPage> & {
  data: Article
}

const RCPOWatchV6: NextPage<RCPOWatchProps> = ({ data, item, modular_content }) => {
  const { t } = useTranslation('navigation')
  const { title, description } = getRcpoModelSeoAttributes(data)
  const router = useRouter()
  const slug = `${slugify(data.modelName as string, { lower: true })}-${data.id as string}`
  const collectionName = data.articleAttributes.find(
    (attr) => attr.attributeID === RCPO_ATTRIBUTE_ID_COLLECTION,
  )?.attributeValue

  return (
    <Page
      title={title}
      description={description as string}
      languageAlternates={buildLanguageAlternateNavigation(
        router.locales,
        'rcpo.selection.url',
        null,
        `/${slug}`,
      )}
    >
      <SchemaOrg variant="rolex-rcpo-product" data={data} />
      <RCPOPage
        pageType="model page"
        productFamily={collectionName.toLowerCase().replace(' ', '-')}
        productRMC={data.articleAttributes.find((attr) => attr.attributeID === 59)?.attributeValue}
      >
        <Breadcrumbs
          items={['index', 'selection']}
          currentPageTitle={title.replace('Rolex Certified Pre-Owned', '').replace(' | GASSAN', '')}
        />

        <ProductInfo data={data} />
        <ProductProperties data={data} />
        {collectionName && (
          <CollectionBanner
            className="mt-0 md:mt-16"
            item={item}
            modularContent={modular_content}
            collectionName={collectionName}
          />
        )}
        <CheckerboardItem
          desktopImage={item.elements.certification_image}
          mobileImage={item.elements.certification_image}
          preTitle="The Rolex Certification"
          title={item.elements.certification_title.value}
          description={item.elements.certification_description.value}
          variant="text-image"
          buttonTitle={t('findOutMore', { ns: 'rolex' })}
          buttonUrl={t('rcpo.certification.url')}
        />
        <ImageBanner
          desktopImage={item.elements.desktop_image_banner}
          mobileImage={item.elements.mobile_image_banner}
        />
        <ContactForm className="py-[5vh]" subject="Question from RCPO Model page" />
        <MayAlsoLike currentProductId={data.id} />
      </RCPOPage>
    </Page>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale, params, preview }) => {
  const id = (params?.pid as string).split('-').pop()
  const lang = locale === 'de' ? 'en' : locale
  const client = createUrqlClient(lang as string, true)
  try {
    const { data } = await client.query(ArticleDocument, { id }).toPromise()
    const content = await getPageByCodename<RolexRcpoModelPage>({
      codename: 'rolex_rcpo___model_page',
      lang: lang as string,
      preview,
    })
    const hasNeededData = hasSufficientProductData(data.article)

    if (!data.article || !data.article.id || !hasNeededData) {
      return { notFound: true }
    }
    return {
      props: {
        ...(await getServerSideTranslations(lang as string)),
        ...content,
        data: data.article,
      },
      revalidate: 10 * 60, // Revalidate every 10 minutes
    }
  } catch (error) {
    throw error
  }
}

export const getStaticPaths = async () => {
  return {
    fallback: 'blocking',
    paths: [],
  }
}

export default RCPOWatchV6
